import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatearPrecio } from "../../../utils/moneda";
import { obtenerConfiguracionTiendaService } from "../../../services/usuarios_tienda/ConfiguracionTienda";
import {
  getPedidos,
  getPedidosResumen,
  updateEstadoPedido,
  confirmarPagoPedido,
  enviarCorreoPedido,
} from "../../../services/usuarios_tienda/pedidos";
import OrderViewModal from "./OrderViewModal";
import { hasPerm } from "../../../utils/hasPerm";
import "./OrdersPage.css";
import OrderDocsModal from "./OrderDocsModalAdmin";
import CustomerViewModal from "./CustomerViewModal";
import { useNavigate, useLocation } from "react-router-dom";
import OrderReceiptModal from "./OrderReceiptModal";
import { obtenerBrandingTiendaSetup } from "../../../services/usuarios_tienda/tiendaSetup";

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500];
const VISIBLE_PAGE_COUNT = 3;

const ESTADO_WORKFLOW_CHIPS = [
  { key: "PENDIENTE", label: "Pendientes", countKey: "pendientes", tone: "pending" },
  { key: "EN_PROCESO", label: "En proceso", countKey: "en_proceso", tone: "processing" },
  { key: "EN_RUTA", label: "En ruta", countKey: "en_ruta", tone: "sent" },
  { key: "ENTREGADO", label: "Entregados", countKey: "entregados", tone: "delivered" },
  { key: "CANCELADO", label: "Cancelados", countKey: "cancelados", tone: "inactive" },
];

const ESTADO_FILTER_OPTIONS = [
  { key: "ALL", label: "Todos", tone: "all" },
  ...ESTADO_WORKFLOW_CHIPS,
];

const PAGO_FILTER_OPTIONS = [
  { value: "ALL", label: "Todos" },
  { value: "NO_PAGADO", label: "No pagados" },
  { value: "PAGADO", label: "Pagados" },
];

const MESES_OPCIONES = [
  { value: "01", label: "Enero" },
  { value: "02", label: "Febrero" },
  { value: "03", label: "Marzo" },
  { value: "04", label: "Abril" },
  { value: "05", label: "Mayo" },
  { value: "06", label: "Junio" },
  { value: "07", label: "Julio" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Septiembre" },
  { value: "10", label: "Octubre" },
  { value: "11", label: "Noviembre" },
  { value: "12", label: "Diciembre" },
];

const ANIO_INICIO = 2020;

function getAnioActual() {
  return new Date().getFullYear();
}

function getMesActual() {
  return String(new Date().getMonth() + 1).padStart(2, "0");
}

function buildAniosOpciones() {
  const actual = getAnioActual();
  const anios = [];
  for (let y = actual; y >= ANIO_INICIO; y -= 1) {
    anios.push(y);
  }
  return anios;
}

function OrdersPage() {
  const [pedidos, setPedidos] = useState([]);
  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("ALL");
  const [pagoFilter, setPagoFilter] = useState("ALL");
  const [anioSeleccionado, setAnioSeleccionado] = useState(getAnioActual);
  const [mesSeleccionado, setMesSeleccionado] = useState(getMesActual);
  const [openPeriodDropdown, setOpenPeriodDropdown] = useState(null);
  const [resumen, setResumen] = useState({
    total: 0,
    pendientes: 0,
    en_proceso: 0,
    en_ruta: 0,
    entregados: 0,
    cancelados: 0,
    pagados: 0,
    no_pagados: 0,
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState({ 1: null });
  const [nextCursorByPage, setNextCursorByPage] = useState({});
  const [showDocs, setShowDocs] = useState(false);
  const [alert, setAlert] = useState(null);
  const [confirmPaymentOrder, setConfirmPaymentOrder] = useState(null);
  const [confirmCancelOrder, setConfirmCancelOrder] = useState(null);
  const [confirmCorreoOrder, setConfirmCorreoOrder] = useState(null);
  const [configuracionTienda, setConfiguracionTienda] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomerPreview, setSelectedCustomerPreview] = useState(null);
  const [receiptOrderId, setReceiptOrderId] = useState(null);
  const [sendingCorreoId, setSendingCorreoId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [storeInfo, setStoreInfo] = useState(null);

  const fmtTotal = useCallback(
    (value) =>
      formatearPrecio(value, configuracionTienda, { espacioEntreSimbolo: true }) ||
      "—",
    [configuracionTienda]
  );

  useEffect(() => {
    let alive = true;
    obtenerConfiguracionTiendaService().then((res) => {
      if (!alive) return;
      if (res.ok && res.data) setConfiguracionTienda(res.data);
    });
    // Nombre/logo de la tienda para el encabezado del recibo/guía.
    obtenerBrandingTiendaSetup().then((res) => {
      if (!alive) return;
      if (res.ok && res.data?.tienda) setStoreInfo(res.data.tienda);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Mensaje de éxito al volver desde la pantalla de "Crear pedido".
  useEffect(() => {
    if (location.state?.createdMsg) {
      setAlert({ type: "success", message: location.state.createdMsg });
      // Limpia el state para que no reaparezca al refrescar o volver atrás.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // La alerta (toast) se cierra sola después de unos segundos.
  useEffect(() => {
    if (!alert) return undefined;
    const t = setTimeout(() => setAlert(null), 4500);
    return () => clearTimeout(t);
  }, [alert]);

  const periodoMes = useMemo(
    () => `${anioSeleccionado}-${mesSeleccionado}`,
    [anioSeleccionado, mesSeleccionado]
  );

  const periodoLabel = useMemo(() => {
    const mesNombre =
      MESES_OPCIONES.find((m) => m.value === mesSeleccionado)?.label || "";
    return `${mesNombre} ${anioSeleccionado}`;
  }, [mesSeleccionado, anioSeleccionado]);

  const cargarResumen = useCallback(async (mes) => {
    try {
      const resp = await getPedidosResumen({ mes: mes || null });
      if (resp?.ok && resp.data) setResumen(resp.data);
    } catch (error) {
      console.error("Error cargando resumen de pedidos:", error);
    }
  }, []);

  useEffect(() => {
    cargarResumen(periodoMes);
  }, [cargarResumen, periodoMes]);

  useEffect(() => {
    if (openPeriodDropdown == null) return;
    const close = () => setOpenPeriodDropdown(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openPeriodDropdown]);

  const aniosOpciones = useMemo(() => buildAniosOpciones(), []);

  const aniosSelectOpciones = useMemo(
    () => aniosOpciones.map((anio) => ({ value: anio, label: String(anio) })),
    [aniosOpciones]
  );

  const mesesDisponibles = useMemo(() => {
    const mesActualNum = Number(getMesActual());
    const esAnioActual = Number(anioSeleccionado) === getAnioActual();

    return MESES_OPCIONES.filter((m) => {
      if (!esAnioActual) return true;
      return Number(m.value) <= mesActualNum;
    });
  }, [anioSeleccionado]);

  useEffect(() => {
    const permitido = mesesDisponibles.some((m) => m.value === mesSeleccionado);
    if (!permitido && mesesDisponibles.length) {
      setMesSeleccionado(mesesDisponibles[mesesDisponibles.length - 1].value);
    }
  }, [mesesDisponibles, mesSeleccionado]);

  useEffect(() => {
    setPage(1);
    setPageCursors({ 1: null });
    setNextCursorByPage({});
    cargarPagina(1);
    // Recarga solo al cambiar filtros; cargarPagina se excluye a propósito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, estadoFilter, pagoFilter, periodoMes]);

  const cargarPagina = async (pageNumber) => {
    try {
      setLoading(true);

      const cursor = pageCursors[pageNumber] ?? null;
      const resp = await getPedidos({
        limit: pageSize,
        cursor,
        estado: estadoFilter === "ALL" ? null : estadoFilter,
        pago: pagoFilter === "ALL" ? null : pagoFilter,
        mes: periodoMes,
      });

      const rows = Array.isArray(resp?.data) ? resp.data : [];
      const nextCursor = resp?.nextCursor || null;

      setPedidos(rows);

      setNextCursorByPage((prev) => ({ ...prev, [pageNumber]: nextCursor }));

      if (nextCursor) {
        setPageCursors((prev) => {
          if (prev[pageNumber + 1] != null) return prev;
          return { ...prev, [pageNumber + 1]: nextCursor };
        });
      }

      setPage(pageNumber);
    } catch (error) {
      console.error("Error cargando pedidos:", error);
    } finally {
      setLoading(false);
    }
  };

  const cambiarEstado = async (pedido_id, estado_nuevo_id) => {
    try {
      setUpdating(true);
      await updateEstadoPedido(pedido_id, { estado_nuevo_id });
      await Promise.all([cargarPagina(page), cargarResumen(periodoMes)]);
    } catch (error) {
      console.error("Error cambiando estado:", error);
  
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "No se pudo actualizar el estado del pedido.";
  
      setAlert({
        type: "error",
        message: msg,
      });
    } finally {
      setUpdating(false);
    }
  };

  const marcarPago = async (pedido_id) => {
    try {
      setUpdating(true);
      await confirmarPagoPedido(pedido_id);
      await Promise.all([cargarPagina(page), cargarResumen(periodoMes)]);
      setAlert({
        type: "success",
        message: "El pedido se marcó como pagado y el stock fue actualizado.",
      });
    } catch (error) {
      console.error("Error confirmando pago:", error);
      const data = error?.response?.data;
      const msg =
        data?.message || error?.message || "Error al confirmar el pago";
      setAlert({
        type: "error",
        message: msg,
        details: formatFaltantes(data?.faltantes),
      });
    } finally {
      setUpdating(false);
    }
  };

  const enviarCorreo = async (pedido_id) => {
    if (sendingCorreoId) return;
    try {
      setSendingCorreoId(pedido_id);
      const res = await enviarCorreoPedido(pedido_id);
      // Marca la fila como enviada sin recargar toda la página.
      setPedidos((prev) =>
        prev.map((p) =>
          p.pedido_id === pedido_id
            ? { ...p, correo_estado: 1, correo_enviado_en: new Date().toISOString() }
            : p
        )
      );
      setAlert({
        type: "success",
        message: res?.message || "Correo del pedido enviado correctamente.",
      });
    } catch (error) {
      console.error("Error enviando correo del pedido:", error);
      setPedidos((prev) =>
        prev.map((p) =>
          p.pedido_id === pedido_id ? { ...p, correo_estado: 2 } : p
        )
      );
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "No se pudo enviar el correo del pedido.";
      setAlert({ type: "error", message: msg });
    } finally {
      setSendingCorreoId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pedidos;

    return pedidos.filter((p) => {
      const pedidoId = String(p.pedido_id ?? "").toLowerCase();
      const clienteCorreo = String(p.correo ?? "").toLowerCase();
      const clienteNom = String(p.cliente_nombre ?? "").toLowerCase();
      return `${pedidoId} ${clienteCorreo} ${clienteNom}`.includes(q);
    });
  }, [pedidos, search]);

  const hasPrev = page > 1;
  const hasNext = Boolean(nextCursorByPage[page]);

  const totalPages = useMemo(() => {
    const keys = Object.keys(pageCursors).map(Number);
    return keys.length ? Math.max(...keys) : 1;
  }, [pageCursors]);

  const visiblePages = useMemo(() => {
    const total = totalPages;
    if (total <= VISIBLE_PAGE_COUNT) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    let start = Math.max(1, page - Math.floor(VISIBLE_PAGE_COUNT / 2));
    let end = start + VISIBLE_PAGE_COUNT - 1;

    if (end > total) {
      end = total;
      start = end - VISIBLE_PAGE_COUNT + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [page, totalPages]);

  const canGoToPage = (pageNumber) =>
    pageNumber === 1 || pageCursors[pageNumber] != null;

  const goToPage = (pageNumber) => {
    if (pageNumber === page || !canGoToPage(pageNumber)) return;
    cargarPagina(pageNumber);
  };

  const nextPage = () => hasNext && cargarPagina(page + 1);
  const prevPage = () => hasPrev && cargarPagina(page - 1);

  return (
    <div className="omx-orders-scope">
      {updating && (
        <div className="overlay">
          <div className="overlay-card">
            <div className="spinner" />
            <div className="overlay-title">Actualizando pedido...</div>
            <div className="overlay-sub">Por favor espera</div>
          </div>
        </div>
      )}

      <div className="header">
        <div className="omx-orders-titlebar">
          <h2 className="title">Pedidos</h2>
          {(hasPerm("create_orders") || hasPerm("move_orders")) && (
            <button
              type="button"
              className="omx-create-order-btn"
              onClick={() => navigate("/administracion/tienda/orders/nuevo")}
            >
              + Crear pedido
            </button>
          )}
        </div>
        <p className="omx-orders-lead">
          Revisá el resumen del período, filtrá por estado y pago, y buscá por pedido o cliente.
        </p>

        <div className="omx-orders-stats">
          <div className="omx-orders-stats__header">
            <span className="omx-orders-stats__title">Resumen del período</span>
            <span className="omx-orders-stats__period">{periodoLabel}</span>
          </div>

          <div className="omx-orders-stats__row" aria-label="Totales por estado de envío">
            {ESTADO_WORKFLOW_CHIPS.map((chip) => (
              <OrderStatSummary key={chip.key} chip={chip} count={resumen[chip.countKey] ?? 0} />
            ))}
          </div>
        </div>

        <EstadoFilterBar value={estadoFilter} onChange={setEstadoFilter} />

        <div className="top-bar">
          <div className="top-bar__periodo" aria-label="Filtrar por período">
            <div className="top-bar__periodo-head">
              <span className="top-bar__periodo-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              </span>
              <span className="top-bar__periodo-title">Período</span>
            </div>

            <div className="top-bar__periodo-controls">
              <PeriodDropdown
                id="orders-mes-filter"
                label="Mes"
                value={mesSeleccionado}
                options={mesesDisponibles}
                isOpen={openPeriodDropdown === "mes"}
                onToggle={(e) => {
                  e.stopPropagation();
                  setOpenPeriodDropdown((prev) => (prev === "mes" ? null : "mes"));
                }}
                onChange={(value) => {
                  setMesSeleccionado(String(value));
                  setOpenPeriodDropdown(null);
                }}
              />

              <PeriodDropdown
                id="orders-anio-filter"
                label="Año"
                value={anioSeleccionado}
                options={aniosSelectOpciones}
                isYear
                isOpen={openPeriodDropdown === "anio"}
                onToggle={(e) => {
                  e.stopPropagation();
                  setOpenPeriodDropdown((prev) => (prev === "anio" ? null : "anio"));
                }}
                onChange={(value) => {
                  setAnioSeleccionado(Number(value));
                  setOpenPeriodDropdown(null);
                }}
              />
            </div>
          </div>

          <div className="top-bar__search" aria-label="Buscar pedidos">
            <div className="top-bar__search-head">
              <span className="top-bar__search-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.5-3.5" />
                </svg>
              </span>
              <span className="top-bar__search-title">Buscar</span>
            </div>

            <div className="top-bar__search-control">
              <input
                type="text"
                placeholder="Pedido, cliente o correo…"
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search.trim() && (
                <button
                  type="button"
                  className="search-input__clear"
                  aria-label="Limpiar búsqueda"
                  onClick={() => setSearch("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="top-bar__pago" aria-label="Filtrar por estado de pago">
            <div className="top-bar__pago-head">
              <span className="top-bar__pago-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M2 10h20" />
                </svg>
              </span>
              <span className="top-bar__pago-title">Pago</span>
            </div>

            <div className="top-bar__pago-controls">
              <PeriodDropdown
                id="orders-pago-filter"
                label="Estado"
                value={pagoFilter}
                options={PAGO_FILTER_OPTIONS}
                isOpen={openPeriodDropdown === "pago"}
                onToggle={(e) => {
                  e.stopPropagation();
                  setOpenPeriodDropdown((prev) => (prev === "pago" ? null : "pago"));
                }}
                onChange={(value) => {
                  setPagoFilter(String(value));
                  setOpenPeriodDropdown(null);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-card-scroll table-card-scroll--desktop">
          <table className="modern-table">
            <thead>
              <tr>
                <th className="th-col-id">#</th>
                <th className="th-col-client">Cliente</th>
                <th className="th-col-ship">Método envío</th>
                <th className="th-col-total">Total</th>
                <th className="th-col-date">Fecha</th>
                <th className="th-col-status">Estado</th>
                <th className="th-col-pay">Pago</th>
                <th className="th-actions th-col-actions">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    Cargando...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    No se encontraron pedidos
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <OrderTableRow
                    key={p.pedido_id}
                    pedido={p}
                    fmtTotal={fmtTotal}
                    updating={updating}
                    onOpenOrder={(id) => {
                      setSelectedOrder(id);
                      setShowModal(true);
                    }}
                    onOpenCustomer={(clienteId, preview) => {
                      setSelectedCustomerId(clienteId);
                      setSelectedCustomerPreview(preview);
                      setShowCustomerModal(true);
                    }}
                    onDocs={(id) => {
                      setSelectedOrder(id);
                      setShowDocs(true);
                    }}
                    onReceipt={setReceiptOrderId}
                    onEnviarCorreo={setConfirmCorreoOrder}
                    sendingCorreoId={sendingCorreoId}
                    onChangeEstado={cambiarEstado}
                    onRequestCancel={setConfirmCancelOrder}
                    onRequestPayment={setConfirmPaymentOrder}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="orders-cards-scroll" aria-label="Listado de pedidos">
          {loading ? (
            <div className="no-data no-data--cards">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="no-data no-data--cards">No se encontraron pedidos</div>
          ) : (
            <div className="orders-cards">
              {filtered.map((p) => (
                <OrderMobileCard
                  key={p.pedido_id}
                  pedido={p}
                  fmtTotal={fmtTotal}
                  updating={updating}
                  onOpenOrder={(id) => {
                    setSelectedOrder(id);
                    setShowModal(true);
                  }}
                  onOpenCustomer={(clienteId, preview) => {
                    setSelectedCustomerId(clienteId);
                    setSelectedCustomerPreview(preview);
                    setShowCustomerModal(true);
                  }}
                  onDocs={(id) => {
                    setSelectedOrder(id);
                    setShowDocs(true);
                  }}
                  onReceipt={setReceiptOrderId}
                  onEnviarCorreo={setConfirmCorreoOrder}
                  sendingCorreoId={sendingCorreoId}
                  onChangeEstado={cambiarEstado}
                  onRequestCancel={setConfirmCancelOrder}
                  onRequestPayment={setConfirmPaymentOrder}
                />
              ))}
            </div>
          )}
        </div>

        {!loading && (
          <div className="pagination">
            <div className="pagination__center">
              <div className="pagination__nav" aria-label="Paginación de pedidos">
                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={prevPage}
                  disabled={!hasPrev}
                  aria-label="Página anterior"
                >
                  ‹
                </button>

                <div className="pagination__pages">
                  {visiblePages.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      className={`pagination__page${
                        pageNumber === page ? " is-active" : ""
                      }`}
                      onClick={() => goToPage(pageNumber)}
                      disabled={!canGoToPage(pageNumber)}
                      aria-label={`Ir a página ${pageNumber}`}
                      aria-current={pageNumber === page ? "page" : undefined}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={nextPage}
                  disabled={!hasNext}
                  aria-label="Página siguiente"
                >
                  ›
                </button>
              </div>

              <div className="pagination__size">
                <span className="pagination__size-label">Mostrar</span>
                <select
                  className="page-size-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  title="Filas por página"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="pagination__size-label">por página</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {alert && (
        <div className={`orders-alert orders-alert-${alert.type}`}>
          <div className="orders-alert-content">
            <span className="orders-alert-title">
              {alert.type === "success" ? "Éxito" : "Atención"}
            </span>
            <span className="orders-alert-message">{alert.message}</span>
            {Array.isArray(alert.details) && alert.details.length > 0 && (
              <ul className="orders-alert-details">
                {alert.details.map((detalle, i) => (
                  <li key={i}>{detalle}</li>
                ))}
              </ul>
            )}
          </div>
          <button
            className="orders-alert-close"
            type="button"
            onClick={() => setAlert(null)}
          >
            ×
          </button>
        </div>
      )}

      {confirmCorreoOrder && (
        <div className="overlay confirm-overlay">
          <div className="overlay-card confirm-card">
            <div className="confirm-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </svg>
            </div>
            <div className="overlay-title">Notificar al cliente</div>
            <div className="overlay-sub">
              {confirmCorreoOrder.correo ? (
                <>
                  Se enviará el correo con el resumen del pedido a{" "}
                  <strong>{confirmCorreoOrder.correo}</strong>. ¿Deseas continuar?
                  {Number(confirmCorreoOrder.correo_estado) === 1 && (
                    <div className="omx-confirm-note">
                      Este pedido ya tenía un correo enviado; se volverá a enviar.
                    </div>
                  )}
                </>
              ) : (
                <span className="omx-confirm-note omx-confirm-note--warn">
                  El cliente de este pedido no tiene un correo registrado, así que
                  no es posible notificarlo.
                </span>
              )}
            </div>
            <div className="confirm-actions">
              <button
                type="button"
                className="btn-state"
                onClick={() => setConfirmCorreoOrder(null)}
                disabled={sendingCorreoId != null}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-state btn-primary is-solid"
                onClick={() => {
                  const id = confirmCorreoOrder.pedido_id;
                  setConfirmCorreoOrder(null);
                  enviarCorreo(id);
                }}
                disabled={sendingCorreoId != null || !confirmCorreoOrder.correo}
              >
                Sí, enviar correo
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmPaymentOrder && (
        <div className="overlay confirm-overlay">
          <div className="overlay-card confirm-card">
            <div className="overlay-title">Marcar como pagado</div>
            <div className="overlay-sub">
              ¿Seguro que deseas marcar este pedido como pagado y actualizar el
              stock?
            </div>
            <div className="confirm-actions">
              <button
                type="button"
                className="btn-state"
                onClick={() => setConfirmPaymentOrder(null)}
                disabled={updating}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-state btn-primary is-solid"
                onClick={() => {
                  const id = confirmPaymentOrder;
                  setConfirmPaymentOrder(null);
                  marcarPago(id);
                }}
                disabled={updating}
              >
                Sí, marcar como pagado
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmCancelOrder && (
        <div className="overlay confirm-overlay">
          <div className="overlay-card confirm-card">
            <div className="confirm-icon confirm-icon--danger" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.3 3.9 1.8 18a1.7 1.7 0 0 0 1.5 2.5h17.4a1.7 1.7 0 0 0 1.5-2.5L13.7 3.9a1.7 1.7 0 0 0-3 0Z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
            </div>
            <div className="overlay-title">Cancelar pedido</div>
            <div className="overlay-sub">
              ¿Seguro que deseas cancelar este pedido? Esta acción no se puede
              deshacer.
            </div>
            <div className="confirm-actions">
              <button
                type="button"
                className="btn-state"
                onClick={() => setConfirmCancelOrder(null)}
                disabled={updating}
              >
                No, volver
              </button>
              <button
                type="button"
                className="btn-state btn-danger is-solid"
                onClick={() => {
                  const id = confirmCancelOrder;
                  setConfirmCancelOrder(null);
                  cambiarEstado(id, ESTADOS.CANCELADO);
                }}
                disabled={updating}
              >
                Sí, cancelar pedido
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <OrderViewModal
          pedidoId={selectedOrder}
          configuracionTienda={configuracionTienda}
          onClose={() => setShowModal(false)}
        />
      )}

      {showDocs && (
        <OrderDocsModal
          pedidoId={selectedOrder}
          onClose={() => setShowDocs(false)}
        />
      )}

      {receiptOrderId != null && (
        <OrderReceiptModal
          pedidoId={receiptOrderId}
          configuracionTienda={configuracionTienda}
          storeInfo={storeInfo}
          onClose={() => setReceiptOrderId(null)}
        />
      )}

      {showCustomerModal && (
        <CustomerViewModal
          clienteId={selectedCustomerId}
          preview={selectedCustomerPreview}
          onClose={() => {
            setShowCustomerModal(false);
            setSelectedCustomerId(null);
            setSelectedCustomerPreview(null);
          }}
        />
      )}

    </div>
  );
}

export default OrdersPage;

function OrderStatSummary({ chip, count }) {
  const toneClass = chip.tone ? ` is-${chip.tone}` : "";

  return (
    <div className={`omx-orders-stat${toneClass}`}>
      <span className="omx-orders-stat__count">{count}</span>
      <span className="omx-orders-stat__label">{chip.label}</span>
    </div>
  );
}

function EstadoFilterBar({ value, onChange }) {
  return (
    <div className="omx-estado-filter" role="group" aria-label="Filtrar pedidos por estado">
      <span className="omx-estado-filter__label">Ver</span>
      <div className="omx-estado-filter__options">
        {ESTADO_FILTER_OPTIONS.map((opt) => {
          const toneClass = opt.tone && opt.tone !== "all" ? ` is-${opt.tone}` : "";
          return (
            <button
              key={opt.key}
              type="button"
              className={`omx-estado-filter__btn${
                value === opt.key ? " is-active" : ""
              }${toneClass}`}
              aria-pressed={value === opt.key}
              onClick={() => onChange(opt.key)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const PERIOD_MENU_MAX_HEIGHT = 280;

function PeriodDropdown({
  id,
  label,
  value,
  options,
  isOpen,
  onToggle,
  onChange,
  isYear = false,
}) {
  const triggerRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const selectedLabel =
    options.find((opt) => String(opt.value) === String(value))?.label ?? "—";

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setMenuStyle(null);
      return;
    }

    const updateMenuPosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < PERIOD_MENU_MAX_HEIGHT + 12;

      setMenuStyle({
        position: "fixed",
        top: openUp ? rect.top - PERIOD_MENU_MAX_HEIGHT - 6 : rect.bottom + 6,
        left: rect.left,
        minWidth: Math.max(rect.width, isYear ? 108 : 168),
        maxHeight: PERIOD_MENU_MAX_HEIGHT,
        zIndex: 10050,
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, isYear]);

  return (
    <div
      className={`period-filter period-dropdown${isYear ? " period-filter--year" : ""}${
        isOpen ? " is-open" : ""
      }`}
    >
      <span className="period-filter__label" id={`${id}-label`}>
        {label}
      </span>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className="period-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={`${id}-label`}
        onClick={onToggle}
      >
        <span className="period-dropdown__value">{selectedLabel}</span>
        <span className="period-dropdown__chevron" aria-hidden="true" />
      </button>

      {isOpen &&
        menuStyle &&
        createPortal(
          <div
            className="period-dropdown__menu period-dropdown__menu--portal"
            style={menuStyle}
            role="listbox"
            aria-labelledby={`${id}-label`}
            onClick={(e) => e.stopPropagation()}
          >
            {options.map((opt) => {
              const selected = String(opt.value) === String(value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`period-dropdown__option${selected ? " is-selected" : ""}`}
                  onClick={() => onChange(opt.value)}
                >
                  <span className="period-dropdown__option-text">{opt.label}</span>
                  {selected && (
                    <span className="period-dropdown__option-check" aria-hidden="true">
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}

// Al confirmar un pago el backend puede responder 409 con un arreglo `faltantes`
// que explica, producto por producto, por qué no se pudo descontar el inventario.
// Cada entrada trae `motivo`, y en el caso de stock insuficiente además
// `requerido` y `disponible`.
function formatFaltantes(faltantes) {
  if (!Array.isArray(faltantes) || faltantes.length === 0) return null;

  return faltantes.map((f) => {
    const nombre =
      f?.nombre_producto || `Producto #${f?.producto_id ?? "sin identificar"}`;
    const motivo = f?.motivo || "No se pudo descontar.";
    const hayCantidades = f?.requerido != null && f?.disponible != null;

    return hayCantidades
      ? `${nombre}: ${motivo} Requiere ${f.requerido}, disponible ${f.disponible}.`
      : `${nombre}: ${motivo}`;
  });
}

function parseEstadoPago(raw) {
  const v = String(raw || "").trim().toUpperCase();
  const isPaid =
    v === "PAGADO" ||
    v === "PAID" ||
    v === "COMPLETED" ||
    v === "COMPLETADO";

  if (isPaid) {
    return {
      label: "Pagado",
      badgeClass: "paid",
      isPaid: true,
      showMarcarPagado: false,
    };
  }

  if (v === "AUTORIZADO" || v === "AUTHORIZED") {
    return {
      label: "Autorizado",
      badgeClass: "processing",
      isPaid: false,
      showMarcarPagado: false,
    };
  }

  if (v === "FALLIDO" || v === "FAILED") {
    return {
      label: "Fallido",
      badgeClass: "inactive",
      isPaid: false,
      showMarcarPagado: false,
    };
  }

  if (v === "REEMBOLSADO" || v === "REFUNDED") {
    return {
      label: "Reembolsado",
      badgeClass: "inactive",
      isPaid: false,
      showMarcarPagado: false,
    };
  }

  return {
    label: "No pagado",
    badgeClass: "pending",
    isPaid: false,
    showMarcarPagado: true,
  };
}

function orderEnvioBadgeClass(p) {
  const code = String(p.codigo_estado_pedido || "").trim().toUpperCase();
  switch (code) {
    case "PENDIENTE":
      return "pending";
    case "EN_PROCESO":
      return "processing";
    case "EN_RUTA":
      return "sent";
    case "ENTREGADO":
      return "active";
    case "CANCELADO":
      return "inactive";
    default:
      break;
  }
  const n = String(p.estado_pedido || "").toLowerCase();
  if (n.includes("entreg")) return "active";
  if (n.includes("cancel")) return "inactive";
  if (n.includes("ruta")) return "sent";
  if (n.includes("proceso")) return "processing";
  return "pending";
}

const ESTADOS = {
  PENDIENTE: 1,
  EN_PROCESO: 2,
  EN_RUTA: 3,
  ENTREGADO: 4,
  CANCELADO: 5,
};

function getWorkflowAction(estado) {
  switch (Number(estado)) {
    case ESTADOS.PENDIENTE:
      return hasPerm("move_orders")
        ? {
            label: "Procesar",
            nextEstado: ESTADOS.EN_PROCESO,
            tone: "process",
            title: "Pasar el pedido a En proceso",
          }
        : null;
    case ESTADOS.EN_PROCESO:
      return hasPerm("move_orders")
        ? {
            label: "Enviar",
            nextEstado: ESTADOS.EN_RUTA,
            tone: "ship",
            title: "Pasar el pedido a En ruta",
          }
        : null;
    case ESTADOS.EN_RUTA:
      return hasPerm("move_orders")
        ? {
            label: "Entregar",
            nextEstado: ESTADOS.ENTREGADO,
            tone: "deliver",
            title: "Marcar el pedido como Entregado",
          }
        : null;
    default:
      return null;
  }
}

function canCancelOrder(estado) {
  const e = Number(estado);
  return (
    (e === ESTADOS.PENDIENTE ||
      e === ESTADOS.EN_PROCESO ||
      e === ESTADOS.EN_RUTA) &&
    hasPerm("cancel_orders")
  );
}

function getOrderDisplayFields(pedido) {
  const orderStatusText =
    pedido.estado_pedido || pedido.estado_texto || "Desconocido";
  const payParsed = parseEstadoPago(pedido.estado_pago);
  const clienteNombre =
    pedido.cliente_nombre || `Cliente #${pedido.cliente_id}`;
  const fechaRaw = String(pedido.fecha_creado || "").split("T")[0];
  const fechaFmt = fechaRaw ? fechaRaw.split("-").reverse().join("/") : "—";
  const shippingMethod =
    pedido.metodo_envio || pedido.nombre_metodo_envio || "—";

  return {
    orderStatusText,
    payParsed,
    clienteNombre,
    fechaRaw,
    fechaFmt,
    shippingMethod,
    envioBadgeClass: orderEnvioBadgeClass(pedido),
    canOpenOrder: hasPerm("view_orders"),
  };
}

function OrderPaymentCell({ pedido, payParsed, updating, onRequestPayment }) {
  return (
    <div className="payment-cell">
      <span className={`status ${payParsed.badgeClass}`}>{payParsed.label}</span>

      {payParsed.showMarcarPagado && hasPerm("move_orders") && (
        <button
          type="button"
          className="btn-state btn-primary is-solid btn-mark-paid"
          disabled={updating}
          onClick={() => onRequestPayment(pedido.pedido_id)}
          title="Marcar pago como pagado"
        >
          Marcar pagado
        </button>
      )}
    </div>
  );
}

function OrderTableRow({
  pedido,
  fmtTotal,
  updating,
  onOpenOrder,
  onOpenCustomer,
  onDocs,
  onReceipt,
  onEnviarCorreo,
  sendingCorreoId,
  onChangeEstado,
  onRequestCancel,
  onRequestPayment,
}) {
  const {
    orderStatusText,
    payParsed,
    clienteNombre,
    fechaRaw,
    fechaFmt,
    shippingMethod,
    envioBadgeClass,
    canOpenOrder,
  } = getOrderDisplayFields(pedido);

  return (
    <tr
      className={`modern-table__row${canOpenOrder ? " is-clickable" : ""}`}
      onClick={canOpenOrder ? () => onOpenOrder(pedido.pedido_id) : undefined}
      title={canOpenOrder ? "Ver detalle del pedido" : undefined}
    >
      <td className="td-col-id">
        <span className="omx-order-id">{pedido.pedido_id}</span>
      </td>

      <td
        className="td-col-client td-col-client--link"
        onClick={(e) => {
          e.stopPropagation();
          if (!pedido.cliente_id) return;
          onOpenCustomer(pedido.cliente_id, {
            cliente_nombre: clienteNombre,
            correo: pedido.correo || "",
          });
        }}
        title="Ver datos del cliente"
      >
        <div className="customer-name">{clienteNombre}</div>
        <div className="customer-email">{pedido.correo || "—"}</div>
      </td>

      <td className="td-col-ship">
        <span className="omx-cell-muted">{shippingMethod}</span>
      </td>

      <td className="td-col-total omx-td-strong">
        {fmtTotal(pedido.total_factura)}
      </td>

      <td className="td-col-date dates">
        <time dateTime={fechaRaw || undefined}>{fechaFmt}</time>
      </td>

      <td className="td-col-status">
        <span
          className={`status ${envioBadgeClass}`}
          title={
            pedido.codigo_estado_pedido
              ? String(pedido.codigo_estado_pedido)
              : undefined
          }
        >
          {orderStatusText}
        </span>
      </td>

      <td className="td-col-pay" onClick={(e) => e.stopPropagation()}>
        <OrderPaymentCell
          pedido={pedido}
          payParsed={payParsed}
          updating={updating}
          onRequestPayment={onRequestPayment}
        />
      </td>

      <td className="td-col-actions" onClick={(e) => e.stopPropagation()}>
        <OrderRowActions
          pedido={pedido}
          updating={updating}
          layout="table"
          onDocs={() => onDocs(pedido.pedido_id)}
          onReceipt={onReceipt}
          onEnviarCorreo={onEnviarCorreo}
          sendingCorreoId={sendingCorreoId}
          onChangeEstado={onChangeEstado}
          onRequestCancel={onRequestCancel}
        />
      </td>
    </tr>
  );
}

function OrderMobileCard({
  pedido,
  fmtTotal,
  updating,
  onOpenOrder,
  onOpenCustomer,
  onDocs,
  onReceipt,
  onEnviarCorreo,
  sendingCorreoId,
  onChangeEstado,
  onRequestCancel,
  onRequestPayment,
}) {
  const {
    orderStatusText,
    payParsed,
    clienteNombre,
    fechaRaw,
    fechaFmt,
    shippingMethod,
    envioBadgeClass,
    canOpenOrder,
  } = getOrderDisplayFields(pedido);

  return (
    <article
      className={`omx-order-card${canOpenOrder ? " is-clickable" : ""}`}
      onClick={canOpenOrder ? () => onOpenOrder(pedido.pedido_id) : undefined}
      title={canOpenOrder ? "Ver detalle del pedido" : undefined}
    >
      <header className="omx-order-card__header">
        <span className="omx-order-id">#{pedido.pedido_id}</span>
        <span className="omx-order-card__total">{fmtTotal(pedido.total_factura)}</span>
      </header>

      <button
        type="button"
        className="omx-order-card__customer"
        onClick={(e) => {
          e.stopPropagation();
          if (!pedido.cliente_id) return;
          onOpenCustomer(pedido.cliente_id, {
            cliente_nombre: clienteNombre,
            correo: pedido.correo || "",
          });
        }}
        title="Ver datos del cliente"
      >
        <span className="customer-name">{clienteNombre}</span>
        <span className="customer-email">{pedido.correo || "—"}</span>
      </button>

      <dl className="omx-order-card__meta">
        <div className="omx-order-card__meta-row">
          <dt>Fecha</dt>
          <dd>
            <time className="dates" dateTime={fechaRaw || undefined}>
              {fechaFmt}
            </time>
          </dd>
        </div>
        <div className="omx-order-card__meta-row">
          <dt>Envío</dt>
          <dd>{shippingMethod}</dd>
        </div>
      </dl>

      <div className="omx-order-card__badges">
        <span
          className={`status ${envioBadgeClass}`}
          title={
            pedido.codigo_estado_pedido
              ? String(pedido.codigo_estado_pedido)
              : undefined
          }
        >
          {orderStatusText}
        </span>
        <span className={`status ${payParsed.badgeClass}`}>{payParsed.label}</span>
      </div>

      {payParsed.showMarcarPagado && hasPerm("move_orders") && (
        <div
          className="omx-order-card__pay-action"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="btn-state btn-primary is-solid btn-mark-paid omx-order-card__pay-btn"
            disabled={updating}
            onClick={() => onRequestPayment(pedido.pedido_id)}
            title="Marcar pago como pagado"
          >
            Marcar pagado
          </button>
        </div>
      )}

      <footer
        className="omx-order-card__actions"
        onClick={(e) => e.stopPropagation()}
      >
        <OrderRowActions
          pedido={pedido}
          updating={updating}
          layout="card"
          onDocs={() => onDocs(pedido.pedido_id)}
          onReceipt={onReceipt}
          onEnviarCorreo={onEnviarCorreo}
          sendingCorreoId={sendingCorreoId}
          onChangeEstado={onChangeEstado}
          onRequestCancel={onRequestCancel}
        />
      </footer>
    </article>
  );
}

const CORREO_UI = {
  0: { dot: "omx-mail-dot--pending", title: "Correo no enviado. Clic para enviarlo al cliente", aria: "Enviar correo del pedido" },
  1: { dot: "omx-mail-dot--sent", title: "Correo enviado. Clic para reenviar", aria: "Reenviar correo del pedido" },
  2: { dot: "omx-mail-dot--error", title: "Error al enviar el correo. Clic para reintentar", aria: "Reintentar correo del pedido" },
};

const IcoReceipt = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
    <path d="M9 8h6M9 12h6" />
  </svg>
);
const IcoPdf = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);
const IcoCancel = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

// Menú "⋮" de acciones secundarias (portal para no recortarse dentro de la tabla).
function OrderActionsMenu({ items, disabled }) {
  const visibles = items.filter((it) => !it.hidden);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setMenuStyle(null);
      return undefined;
    }
    const update = () => {
      const t = triggerRef.current;
      if (!t) return;
      const r = t.getBoundingClientRect();
      const width = 200;
      const estimated = visibles.length * 42 + 12;
      const openUp = window.innerHeight - r.bottom < estimated + 12;
      setMenuStyle({
        position: "fixed",
        top: openUp ? Math.max(8, r.top - estimated - 6) : r.bottom + 6,
        left: Math.min(Math.max(8, r.right - width), window.innerWidth - width - 8),
        width,
        zIndex: 10050,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, visibles.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest(".omx-menu")) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!visibles.length) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`omx-actions__btn omx-actions__btn--kebab${open ? " is-open" : ""}`}
        title="Más acciones"
        aria-label="Más acciones"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="1.9" />
          <circle cx="12" cy="12" r="1.9" />
          <circle cx="12" cy="19" r="1.9" />
        </svg>
      </button>

      {open &&
        menuStyle &&
        createPortal(
          <div
            className="omx-menu"
            style={menuStyle}
            role="menu"
            onClick={(e) => e.stopPropagation()}
          >
            {visibles.map((it, i) => (
              <button
                key={i}
                type="button"
                role="menuitem"
                className={`omx-menu__item${it.danger ? " omx-menu__item--danger" : ""}`}
                onClick={() => {
                  setOpen(false);
                  it.onClick();
                }}
              >
                <span className="omx-menu__ico">{it.icon}</span>
                <span>{it.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

function OrderRowActions({
  pedido,
  updating,
  onDocs,
  onReceipt,
  onEnviarCorreo,
  sendingCorreoId,
  onChangeEstado,
  onRequestCancel,
  layout = "inline",
}) {
  const pedidoId = pedido.pedido_id;
  const workflow = getWorkflowAction(pedido.estado);
  const showCancel = canCancelOrder(pedido.estado);
  const canDocs = hasPerm("upload_order_docs");
  const canReceipt = hasPerm("view_orders");
  const canCorreo = hasPerm("view_orders");
  const correoEstado = Number(pedido.correo_estado || 0);
  const correoUi = CORREO_UI[correoEstado] || CORREO_UI[0];
  const isSendingCorreo = sendingCorreoId === pedidoId;
  const isTable = layout === "table";
  const isCard = layout === "card";

  const menuItems = [
    { label: "Recibo", onClick: () => onReceipt(pedidoId), hidden: !canReceipt, icon: IcoReceipt },
    { label: "Documentos PDF", onClick: onDocs, hidden: !canDocs, icon: IcoPdf },
    {
      label: "Cancelar pedido",
      onClick: () => onRequestCancel(pedidoId),
      hidden: !showCancel,
      danger: true,
      icon: IcoCancel,
    },
  ];
  const hasMenu = menuItems.some((it) => !it.hidden);
  const hasBar = workflow || canCorreo || hasMenu;

  return (
    <div
      className={`omx-actions${isTable ? " omx-actions--table" : ""}${
        isCard ? " omx-actions--compact" : ""
      }`}
    >
      {hasBar ? (
        <div
          className={`omx-actions__bar${isTable || isCard ? " omx-actions__bar--wrap" : ""}`}
          role="group"
          aria-label="Acciones del pedido"
        >
          {workflow && (
            <button
              type="button"
              className={`omx-actions__btn omx-actions__btn--primary omx-actions__btn--${workflow.tone}`}
              title={workflow.title}
              disabled={updating}
              onClick={() => onChangeEstado(pedidoId, workflow.nextEstado)}
            >
              {workflow.label}
            </button>
          )}

          {canCorreo && (
            <button
              type="button"
              className="omx-actions__btn omx-mailbtn"
              title={correoUi.title}
              aria-label={correoUi.aria}
              disabled={updating || isSendingCorreo}
              onClick={() => onEnviarCorreo(pedido)}
            >
              {isSendingCorreo ? (
                <span className="omx-mail-spin" aria-hidden="true" />
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="M3 7l9 6 9-6" />
                  </svg>
                  <span className={`omx-mail-dot ${correoUi.dot}`} aria-hidden="true" />
                </>
              )}
            </button>
          )}

          <OrderActionsMenu items={menuItems} disabled={updating} />
        </div>
      ) : (
        <span className="omx-actions__empty">—</span>
      )}
    </div>
  );
}